import { useEffect, useRef, useState, useCallback } from "react";
import { create, toBinary, fromBinary } from "@bufbuild/protobuf";
import {
  FleetClientMessageSchema,
  FleetServerMessageSchema,
  SetViewportSchema,
  ViewportBoundsSchema,
  PingSchema,
  type FleetServerMessage,
  type DroneState as ProtoDroneState,
  type PositionDelta,
} from "@drone-fleet/shared/proto";

// Viewport bounds matching Mapbox/OpenLayers format
export interface ViewportBounds {
  ne: { lat: number; lng: number };
  sw: { lat: number; lng: number };
}

// Drone state for UI consumption
export interface FleetDroneState {
  id: string;
  lat: number;
  lng: number;
  alt: number;
  heading: number;
  battery: number;
  connected: boolean;
  lastUpdate: number;
}

export interface UseFleetSocketResult {
  connected: boolean;
  drones: Map<string, FleetDroneState>;
  setViewport: (bounds: ViewportBounds) => void;
  error: string | null;
}

// Message ID counter
let messageIdCounter = 0;

function nextMessageId(): number {
  return ++messageIdCounter;
}

// Convert protobuf DroneState to UI format
function protoToFleetDrone(proto: ProtoDroneState): FleetDroneState {
  return {
    id: proto.id,
    lat: proto.lat,
    lng: proto.lng,
    alt: proto.alt,
    heading: proto.heading,
    battery: proto.battery,
    connected: proto.connected,
    lastUpdate: Number(proto.lastUpdate),
  };
}

// Apply position delta to existing drone state
function applyDelta(
  drone: FleetDroneState,
  delta: PositionDelta
): FleetDroneState {
  return {
    ...drone,
    lat: delta.lat ?? drone.lat,
    lng: delta.lng ?? drone.lng,
    alt: delta.alt ?? drone.alt,
    heading: delta.heading ?? drone.heading,
    lastUpdate: Date.now(),
  };
}

export function useFleetSocket(): UseFleetSocketResult {
  const [connected, setConnected] = useState(false);
  const [drones, setDrones] = useState<Map<string, FleetDroneState>>(
    new Map()
  );
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | undefined>(undefined);
  const pingIntervalRef = useRef<number | undefined>(undefined);
  const pendingViewportRef = useRef<ViewportBounds | null>(null);

  const sendMessage = useCallback((data: Uint8Array) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(data);
    }
  }, []);

  const setViewport = useCallback(
    (bounds: ViewportBounds) => {
      // Store pending viewport in case we're not connected yet
      pendingViewportRef.current = bounds;

      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        return;
      }

      const message = create(FleetClientMessageSchema, {
        messageId: nextMessageId(),
        payload: {
          case: "setViewport",
          value: create(SetViewportSchema, {
            bounds: create(ViewportBoundsSchema, {
              neLat: bounds.ne.lat,
              neLng: bounds.ne.lng,
              swLat: bounds.sw.lat,
              swLng: bounds.sw.lng,
            }),
          }),
        },
      });

      sendMessage(toBinary(FleetClientMessageSchema, message));
    },
    [sendMessage]
  );

  const handleMessage = useCallback((data: ArrayBuffer) => {
    try {
      const message = fromBinary(
        FleetServerMessageSchema,
        new Uint8Array(data)
      ) as FleetServerMessage;

      const { payload } = message;

      switch (payload.case) {
        case "snapshot": {
          // Full state snapshot - replace all drones
          const snapshot = payload.value;
          setDrones(() => {
            const newMap = new Map<string, FleetDroneState>();
            for (const drone of snapshot.drones) {
              newMap.set(drone.id, protoToFleetDrone(drone));
            }
            return newMap;
          });
          break;
        }

        case "delta": {
          // Position updates - apply deltas to existing drones
          const delta = payload.value;
          setDrones((prev) => {
            const newMap = new Map(prev);
            for (const update of delta.updates) {
              const existing = newMap.get(update.id);
              if (existing) {
                newMap.set(update.id, applyDelta(existing, update));
              }
            }
            return newMap;
          });
          break;
        }

        case "droneEntered": {
          // New drone entered viewport
          const entered = payload.value;
          if (entered.drone) {
            const drone = protoToFleetDrone(entered.drone);
            setDrones((prev) => new Map(prev).set(drone.id, drone));
          }
          break;
        }

        case "droneExited": {
          // Drone exited viewport
          const exited = payload.value;
          setDrones((prev) => {
            const newMap = new Map(prev);
            newMap.delete(exited.droneId);
            return newMap;
          });
          break;
        }

        case "droneConnected": {
          // Drone connected
          const connected = payload.value;
          setDrones((prev) => {
            const existing = prev.get(connected.droneId);
            if (existing) {
              const newMap = new Map(prev);
              newMap.set(connected.droneId, {
                ...existing,
                connected: true,
                lastUpdate: Date.now(),
              });
              return newMap;
            }
            return prev;
          });
          break;
        }

        case "droneDisconnected": {
          // Drone disconnected
          const disconnected = payload.value;
          setDrones((prev) => {
            const existing = prev.get(disconnected.droneId);
            if (existing) {
              const newMap = new Map(prev);
              newMap.set(disconnected.droneId, {
                ...existing,
                connected: false,
                lastUpdate: Date.now(),
              });
              return newMap;
            }
            return prev;
          });
          break;
        }

        case "error": {
          const error = payload.value;
          setError(error.message);
          break;
        }

        case "ack":
          // Acknowledgment - no action needed
          break;
      }
    } catch (err) {
      console.error("Failed to parse fleet message:", err);
    }
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const apiUrl = import.meta.env.VITE_API_URL || `${window.location.protocol}//${window.location.host}`;
    const wsUrl = apiUrl.replace(/^http/, "ws") + "/api/fleet/ws";

    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setError(null);

      // Send pending viewport if any
      if (pendingViewportRef.current) {
        setViewport(pendingViewportRef.current);
      }

      // Start ping interval
      pingIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          const ping = create(FleetClientMessageSchema, {
            messageId: nextMessageId(),
            payload: {
              case: "ping",
              value: create(PingSchema, {}),
            },
          });
          ws.send(toBinary(FleetClientMessageSchema, ping));
        }
      }, 30000); // Ping every 30 seconds
    };

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        handleMessage(event.data);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      setDrones(new Map()); // Clear drones on disconnect
      wsRef.current = null;

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = undefined;
      }

      // Attempt reconnect after 3 seconds
      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect();
      }, 3000);
    };

    ws.onerror = () => {
      setError("WebSocket connection error");
    };
  }, [handleMessage, setViewport]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return {
    connected,
    drones,
    setViewport,
    error,
  };
}
